/** @file NumericField: a number field with formulas, arrows, Esc restore, Tab order and a scrub label. */
import type { CSSProperties, KeyboardEvent, ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { axisColors } from './axisColors';
import { formatNumber } from './formatNumber';
import { moveFocus } from './moveFocus';
import { parseNumberInput } from './parseNumberInput';
import type { StepModifiers } from './modifierScale';
import { stepValue } from './stepValue';
import { useScrub } from './useScrub';

/**
 * `value` shown and `onCommit` called with every changed, valid entry (typing, arrows, a scrub release). `label` is the
 * aria-label and, unless `fieldId` is given, the stable `data-field` id Tab finds the next field by. `step` is one arrow
 * press / 6 px of scrub. `handle` is the label that scrubs on drag (an axis letter); `axis` (0 X, 1 Y, 2 Z) colours the
 * left border. `onPreview` shows a scrub in progress, `onCancel` undoes that preview when Esc cancels the drag.
 */
export type NumericFieldProps = {
  value: number; onCommit: (value: number) => void; label: string; fieldId?: string; step?: number;
  disabled?: boolean; muted?: boolean; title?: string; axis?: number; handle?: ReactNode;
  onPreview?: (value: number) => void; onCancel?: () => void;
};

const BORDER = '1px solid var(--p3-border, #2a2e38)';

/** The input's inline style: same width in every row (flex), axis colour on the left border. */
function inputStyle(p: NumericFieldProps): CSSProperties {
  const accent = p.axis === undefined ? undefined : axisColors[p.axis];
  return {
    flex: 1, minWidth: 0, width: '100%', font: 'inherit', fontFamily: 'var(--p3-mono, monospace)', padding: '1px 4px',
    background: 'var(--p3-panel-2, #1f232b)', color: p.muted === true ? 'var(--p3-muted, #8b919e)' : 'inherit',
    border: BORDER, borderLeft: accent === undefined ? BORDER : `3px solid ${accent}`, borderRadius: 3, opacity: p.disabled === true ? 0.5 : 1,
  };
}

/**
 * Text is a local draft. Enter, Tab and blur commit it through `parseNumberInput` (a rejected text reverts); Esc restores
 * the value and keeps focus; Up / Down step by `step` (Shift x10, Alt x0.1); focus selects all. Tab commits, then focuses
 * the next tabbable control by its `data-field` id after the re-render. Dragging `handle` scrubs.
 */
export function NumericField(props: NumericFieldProps): ReactElement {
  const { value, onCommit, step = 1 } = props;
  const [text, setText] = useState(formatNumber(value));
  const input = useRef<HTMLInputElement>(null);
  const pending = useRef<string | null>(null);
  const fresh = useRef(false);
  useEffect(() => { setText(formatNumber(value)); }, [value]);
  const show = (n: number): void => { pending.current = formatNumber(n); setText(pending.current); };
  const commit = (): void => {
    if (text === formatNumber(value) || text === pending.current) return;
    const n = parseNumberInput(text, value);
    if (n === null) { setText(formatNumber(value)); return; }
    show(n);
    if (n !== value) onCommit(n);
  };
  const scrub = useScrub({
    value, step, disabled: props.disabled === true,
    onChange: (n) => { setText(formatNumber(n)); props.onPreview?.(n); },
    onEnd: (n) => { show(n); onCommit(n); },
    onCancel: () => { setText(formatNumber(value)); props.onCancel?.(); },
    onClick: () => { input.current?.focus(); },
  });
  const stepBy = (dir: 1 | -1, m: StepModifiers): void => {
    const n = stepValue(parseNumberInput(text, value) ?? value, dir, step, m);
    show(n);
    if (n !== value) onCommit(n);
  };
  const keys: Record<string, (e: KeyboardEvent<HTMLInputElement>) => void> = {
    Enter: (e) => { commit(); e.currentTarget.select(); },
    NumpadEnter: (e) => { commit(); e.currentTarget.select(); },
    Escape: (e) => { e.stopPropagation(); pending.current = null; setText(formatNumber(value)); e.currentTarget.select(); },
    ArrowUp: (e) => { stepBy(1, e); },
    ArrowDown: (e) => { stepBy(-1, e); },
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.code === 'Tab') { commit(); if (moveFocus(e.currentTarget, e.shiftKey ? -1 : 1)) e.preventDefault(); return; }
    const handler = keys[e.code];
    if (handler === undefined) return;
    e.preventDefault();
    handler(e);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1, minWidth: 0 }}>
      {props.handle !== undefined && (
        <span tabIndex={-1} data-scrub={props.fieldId ?? props.label} {...scrub.props} title="Drag to change"
          style={{ cursor: props.disabled === true ? 'default' : 'ew-resize', userSelect: 'none', touchAction: 'none', outline: 'none', minWidth: 10, textAlign: 'center',
            color: scrub.scrubbing ? 'var(--p3-accent, #f2b544)' : 'var(--p3-muted, #8b919e)' }}>
          {props.handle}
        </span>
      )}
      <input ref={input} type="text" inputMode="decimal" autoComplete="off" spellCheck={false} aria-label={props.label} title={props.title}
        data-field={props.fieldId ?? props.label} value={text} disabled={props.disabled} style={inputStyle(props)}
        onChange={(e) => { pending.current = null; setText(e.currentTarget.value); }}
        onFocus={(e) => { fresh.current = true; pending.current = null; e.currentTarget.select(); }}
        onMouseUp={(e) => { if (fresh.current) e.preventDefault(); fresh.current = false; }}
        onBlur={() => { fresh.current = false; commit(); }} onKeyDown={onKeyDown} />
    </div>
  );
}
